/**
 * The studio takes an order. Spec 006.
 *
 * An order could reach this system two ways, a Shopify webhook or the
 * historical import, and both are machines. The business takes orders on the
 * telephone, by email, through Facebook and through Instagram, and every one
 * of them had to be typed into Shopify to come back through the bridge, or
 * written on paper.
 *
 * ── WHAT THIS SCREEN IS CAREFUL ABOUT ───────────────────────────────────────
 *
 *   The source is required. It answers "how did the customer reach us", which
 *   is what decides where Guy spends his time. It is NOT the channel: channel
 *   is attribution and decides commission, so it is never offered as a choice.
 *
 *   The total is shown but never sent. The server recomputes it from the
 *   quantities and the prices, so what is on screen is a preview of an answer
 *   the server gives.
 *
 *   An invoiced order is not a paid order. The tick says so in words.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { Button, Callout, Field, inputClass, textareaClass } from '@/components/ui';
import { toPostalAddress } from '@/lib/address';
import { formatCents } from '@/lib/money';
import { gstComponent } from '@/lib/money';
import { newOrderQuery, orderSourcesQuery } from '@/queries';

interface Line {
  published_id: string | null;
  title: string;
  qty: string;
  price: string;
  note: string;
}

const BLANK: Line = { published_id: null, title: '', qty: '1', price: '', note: '' };

export function NewOrder() {
  const [open, setOpen] = useState(false);
  if (!open) return <Button onClick={() => setOpen(true)}>New order</Button>;
  return (
    <div className="w-full">
      <Form onClose={() => setOpen(false)} />
    </div>
  );
}

function Form({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const catalogue = useQuery(newOrderQuery);
  const sources = useQuery(orderSourcesQuery);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [source, setSource] = useState('');
  const [settlement, setSettlement] = useState<'invoice' | 'bank_transfer' | 'stripe'>('invoice');
  const [paid, setPaid] = useState(false);
  const [collecting, setCollecting] = useState(true);
  const [line1, setLine1] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('QLD');
  const [postcode, setPostcode] = useState('');
  const [shipping, setShipping] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<Line[]>([{ ...BLANK }]);
  const [error, setError] = useState<string | null>(null);
  const [known, setKnown] = useState<string | null>(null);

  /**
   * Spec 016 FR-1607. An email that a customer already has fills the company,
   * the person and the delivery address. Only the empty fields are filled, so
   * nothing a person typed is lost, and every field stays editable for this
   * order. A person who may not see customers gets nothing filled, and the
   * server still fills the address when the street is left empty.
   */
  async function lookUp() {
    const typed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(typed)) return;
    const res = await fetch(`/api/customers/lookup?email=${encodeURIComponent(typed)}`,
      { credentials: 'same-origin' }).catch(() => null);
    if (!res?.ok) return;
    const body = (await res.json().catch(() => null)) as {
      customer: { name: string; kind: string; phone: string | null } | null;
      contact?: { name: string; phone: string | null } | null;
      delivery_address?: Record<string, string> | null;
    } | null;
    if (!body?.customer) { setKnown(null); return; }
    const c = body.customer;
    if (c.kind === 'b2b' || body.contact) {
      if (!company.trim()) setCompany(c.name);
      if (!name.trim() && body.contact?.name) setName(body.contact.name);
    } else if (!name.trim()) {
      setName(c.name);
    }
    if (!phone.trim()) setPhone(body.contact?.phone || c.phone || '');
    const at = body.delivery_address ? toPostalAddress(body.delivery_address) : null;
    if (at?.line1 && !line1.trim()) {
      setCollecting(false);
      setLine1([at.line1, at.line2].filter(Boolean).join(', '));
      setSuburb(at.suburb);
      setState(at.state || state);
      setPostcode(at.postcode);
    }
    setKnown(at?.line1
      ? `${c.name} is a customer. The delivery address is from their record. Change it here for this order only.`
      : `${c.name} is a customer. They have no delivery address on their record.`);
  }

  const products = catalogue.data?.published ?? [];

  const cents = (dollars: string) => Math.round((Number(dollars) || 0) * 100);

  const money = useMemo(() => {
    const goods = lines.reduce((n, l) => n + cents(l.price) * (Number(l.qty) || 0), 0);
    const ship = collecting ? 0 : cents(shipping);
    const total = goods + ship;
    return { goods, ship, total, gst: gstComponent(total) };
  }, [lines, shipping, collecting]);

  const set = (i: number, patch: Partial<Line>) =>
    setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  /** Choosing a product fills the description and the price, both editable. */
  function choose(i: number, id: string) {
    const p = products.find((x) => x.id === id);
    if (!p) { set(i, { published_id: null }); return; }
    set(i, {
      published_id: p.id,
      title: p.title,
      price: ((p.retail_price_cents ?? 0) / 100).toFixed(2),
    });
  }

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/orders', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          customer: {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || undefined,
            company: company.trim() || undefined,
          },
          source,
          settlement,
          paid,
          collecting,
          shipping_cents: collecting ? 0 : cents(shipping),
          shipping_address: collecting ? null : {
            line1: line1.trim(), suburb: suburb.trim(),
            state: state.trim(), postcode: postcode.trim(),
          },
          lines: lines.map((l) => ({
            published_id: l.published_id,
            title: l.title.trim(),
            qty: Number(l.qty) || 0,
            unit_price_cents: cents(l.price),
            note: l.note.trim() || undefined,
          })),
          note: note.trim() || undefined,
        }),
      });
      const body = (await res.json().catch(() => null)) as
        | { ref?: string; message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      return body as { ref: string; message: string };
    },
    onSuccess: async (made) => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'order-list'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
      await navigate({ to: '/admin/orders/$ref', params: { ref: made.ref } });
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) return setError('Who is this order for?');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      return setError('Use an email address the studio can reply to.');
    }
    if (!source) return setError('Say how the customer reached us.');
    if (lines.some((l) => !l.title.trim())) return setError('Every line needs a description.');
    if (lines.some((l) => !(Number(l.qty) > 0))) return setError('Every line needs a quantity.');
    const free = lines.find((l) => cents(l.price) === 0 && !l.note.trim());
    if (free) {
      return setError(
        `"${free.title || 'A line'}" is priced at nothing and does not say why. `
        + 'A free replacement is fine; add a note so the month end reads correctly.',
      );
    }
    setError(null);
    create.mutate();
  }

  return (
    <form onSubmit={submit} className="rounded-[10px] border border-line bg-panel p-5" aria-label="Take an order">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold">Take an order</h2>
          <p className="mt-1 text-[12.5px] text-muted">
            For an order that came in by telephone, by email or through social
            media. Stock is held the moment it is saved.
          </p>
        </div>
        <Button type="button" weight="secondary" onClick={onClose}>Cancel</Button>
      </div>

      {error && <div className="mt-4"><Callout tone="danger" title={error} /></div>}

      {known && <p className="mt-4 text-[13px] text-muted" role="status">{known}</p>}

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field label="Customer" htmlFor="no-name">
          <input id="no-name" className={inputClass} value={name}
            onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Company" htmlFor="no-company" optional
          hint="A school or a club. It becomes the name on the order.">
          <input id="no-company" className={inputClass} value={company}
            onChange={(e) => setCompany(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Email" htmlFor="no-email" hint="Where the confirmation goes.">
          <input id="no-email" type="email" className={inputClass} value={email}
            onChange={(e) => setEmail(e.target.value)} onBlur={() => void lookUp()}
            autoComplete="off" />
        </Field>
        <Field label="Phone" htmlFor="no-phone" optional>
          <input id="no-phone" className={inputClass} value={phone}
            onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
        </Field>

        <Field label="How did they reach us" htmlFor="no-source"
          hint="What this answers is where the marketing is working.">
          <select id="no-source" className={inputClass} value={source}
            onChange={(e) => setSource(e.target.value)}>
            <option value="">Choose one</option>
            {(sources.data?.sources ?? []).map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </Field>

        <Field label="How it settles" htmlFor="no-settlement">
          <select id="no-settlement" className={inputClass} value={settlement}
            onChange={(e) => setSettlement(e.target.value as typeof settlement)}>
            <option value="invoice">On invoice</option>
            <option value="bank_transfer">Bank transfer</option>
            <option value="stripe">Card</option>
          </select>
        </Field>
      </div>

      <fieldset className="mt-6">
        <legend className="text-sm font-semibold">What they ordered</legend>
        <p className="mt-1 text-[13px] text-muted">
          Choose a product, or leave it blank and describe the item. A described
          line holds no stock, because nothing on a shelf answers to it.
        </p>

        <div className="mt-3 space-y-3">
          {lines.map((l, i) => (
            <div key={i} className="rounded-[8px] border border-line p-3">
              <div className="grid gap-3 sm:grid-cols-[2fr_2fr_70px_110px]">
                <select
                  aria-label={`Line ${i + 1} product`}
                  className={inputClass}
                  value={l.published_id ?? ''}
                  onChange={(e) => choose(i, e.target.value)}
                >
                  <option value="">Not in the catalogue</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
                <input
                  aria-label={`Line ${i + 1} description`}
                  className={inputClass} placeholder="What it is"
                  value={l.title} onChange={(e) => set(i, { title: e.target.value })}
                />
                <input
                  aria-label={`Line ${i + 1} quantity`}
                  className={inputClass} inputMode="numeric" value={l.qty}
                  onChange={(e) => set(i, { qty: e.target.value.replace(/[^0-9]/g, '') })}
                />
                <input
                  aria-label={`Line ${i + 1} unit price`}
                  className={inputClass} inputMode="decimal" placeholder="0.00"
                  value={l.price}
                  onChange={(e) => set(i, { price: e.target.value.replace(/[^0-9.]/g, '') })}
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <input
                  aria-label={`Line ${i + 1} note`}
                  className={`${inputClass} max-w-[420px]`}
                  placeholder="Note, needed if this line is free of charge"
                  value={l.note} onChange={(e) => set(i, { note: e.target.value })}
                />
                <span className="text-[13px] text-muted">
                  {formatCents(cents(l.price) * (Number(l.qty) || 0))}
                </span>
                {lines.length > 1 && (
                  <Button type="button" weight="tertiary"
                    onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3">
          <Button type="button" weight="secondary" onClick={() => setLines([...lines, { ...BLANK }])}>
            Add a line
          </Button>
        </div>
      </fieldset>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={collecting}
              onChange={(e) => setCollecting(e.target.checked)} />
            They are collecting it
          </label>

          {!collecting && (
            <div className="mt-3 grid gap-3">
              <Field label="Street address" htmlFor="no-line1">
                <input id="no-line1" className={inputClass} value={line1}
                  onChange={(e) => setLine1(e.target.value)} />
              </Field>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Suburb" htmlFor="no-suburb">
                  <input id="no-suburb" className={inputClass} value={suburb}
                    onChange={(e) => setSuburb(e.target.value)} />
                </Field>
                <Field label="State" htmlFor="no-state">
                  <input id="no-state" className={inputClass} value={state}
                    onChange={(e) => setState(e.target.value)} />
                </Field>
                <Field label="Postcode" htmlFor="no-postcode">
                  <input id="no-postcode" className={inputClass} value={postcode}
                    onChange={(e) => setPostcode(e.target.value)} />
                </Field>
              </div>
              <Field label="Shipping" htmlFor="no-shipping" hint="In dollars.">
                <input id="no-shipping" className={inputClass} inputMode="decimal"
                  value={shipping} placeholder="0.00"
                  onChange={(e) => setShipping(e.target.value.replace(/[^0-9.]/g, ''))} />
              </Field>
            </div>
          )}

          <div className="mt-4">
            <Field label="Note" htmlFor="no-note" optional
              hint="What was agreed. It goes on the order history.">
              <textarea id="no-note" className={textareaClass} value={note}
                onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="rounded-[10px] border border-line bg-raised p-5">
          <dl className="space-y-2 text-[15px]">
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Goods</dt>
              <dd className="font-semibold">{formatCents(money.goods)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Shipping</dt>
              <dd className="font-semibold">
                {money.ship === 0 ? (collecting ? 'Collecting' : 'Free') : formatCents(money.ship)}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-t border-line pt-3 text-lg">
              <dt className="font-bold">Total</dt>
              <dd className="font-extrabold">{formatCents(money.total)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-[13px] text-muted">GST included</dt>
              <dd className="text-[13px] text-muted">{formatCents(money.gst)}</dd>
            </div>
          </dl>

          <label className="mt-4 flex items-start gap-2 text-[13px]">
            <input type="checkbox" checked={paid} className="mt-1"
              onChange={(e) => setPaid(e.target.checked)} />
            <span>
              They have already paid.
              {!paid && (
                <span className="block text-muted">
                  Leave this clear and the order waits for payment, which is
                  what puts it on the debtors list.
                </span>
              )}
            </span>
          </label>

          <p className="mt-3 text-[12.5px] leading-relaxed text-muted">
            The total is worked out again when this is saved, from the
            quantities and the prices. Stock is held at the same moment, so an
            order that cannot be filled is refused in full rather than half
            written.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Saving...' : 'Take the order'}
        </Button>
        {catalogue.isError && (
          <span className="text-[13px] text-danger">
            The catalogue did not load, so only described lines can be added.
          </span>
        )}
      </div>
    </form>
  );
}
